import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { propertiesApiClient } from '@/lib/api/properties.client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Edit,
  Trash2,
  Copy,
  MoreHorizontal,
  UserCheck,
  Search,
  FolderOpen,
  Zap,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterConditionsSummary } from '@/components/common/FilterConditionsSummary'
import type { Property } from '@super/shared-types'
import {
  HandoffWorkflowsProvider,
  useHandoffWorkflows,
  type HandoffRule,
} from '@/contexts/handoff-workflows.context'
import { DirectoryProvider } from '@/contexts/directory.context'
import Fuse from 'fuse.js'

function HandoffRulesTable() {
  const navigate = useNavigate()
  const { handoffRules, loading, deleteWorkflow, duplicateWorkflow } =
    useHandoffWorkflows()
  const [searchQuery, setSearchQuery] = useState('')

  const fuse = useMemo(
    () =>
      new Fuse(handoffRules, {
        keys: [
          'title',
          'handoffInstructions',
          'propertyNames',
          'selectionCriteria',
          'actionGroups.selectionCriteria',
        ],
        threshold: 0.1,
        includeScore: true,
        ignoreLocation: true,
        findAllMatches: true,
      }),
    [handoffRules],
  )

  const filteredHandoffRules = useMemo(() => {
    if (!searchQuery.trim()) {
      return handoffRules
    }

    const query = searchQuery.toLowerCase()

    const exactMatches = handoffRules.filter(
      rule =>
        rule.title.toLowerCase().includes(query) ||
        rule.handoffInstructions.toLowerCase().includes(query) ||
        rule.propertyNames?.some(name => name.toLowerCase().includes(query)) ||
        rule.actionGroups.some(group =>
          group.selectionCriteria?.toLowerCase().includes(query),
        ),
    )

    const fuzzyResults = fuse.search(searchQuery)
    const fuzzyMatches = fuzzyResults
      .map(result => result.item)
      .filter(item => !exactMatches.some(exact => exact.id === item.id))

    return [...exactMatches, ...fuzzyMatches]
  }, [handoffRules, searchQuery, fuse])

  const getActionsSummary = (rule: HandoffRule) => {
    const actionGroupCount = rule.actionGroups.length
    // A TRANSFER mirrors the group's PHONE_CALL rows, so it is not counted.
    const totalActions = rule.actionGroups.reduce(
      (sum, group) =>
        sum +
        (group.actions?.filter(
          action => action.actionDefinition?.actionType !== 'TRANSFER',
        ).length || 0),
      0,
    )

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <FolderOpen className="h-3 w-3" />
          <span>
            {actionGroupCount} action group{actionGroupCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Zap className="h-3 w-3" />
          <span>
            {totalActions} action{totalActions !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    )
  }

  const getConditionsSummary = (rule: HandoffRule) => (
    <FilterConditionsSummary
      officeHours={rule.officeHours}
      propertyCount={rule.propertyIds?.length ?? 0}
      propertyGroupCount={rule.propertyGroupIds?.length ?? 0}
      singlePropertyName={rule.propertyNames[0]}
      groupTypes={rule.groupTypes ?? []}
    />
  )

  const handleCreate = () => navigate('/actions/handoff/new')
  const handleEdit = (rule: HandoffRule) =>
    navigate(`/actions/handoff/${rule.id}`)

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
          <div className="relative w-96 max-w-96">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search handoff rules..."
              disabled
              className="pl-9"
            />
          </div>
          <Button disabled variant="form">
            Add handoff rule
          </Button>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Handoff rule</TableHead>
                <TableHead className="w-[180px] hidden lg:table-cell">
                  Actions
                </TableHead>
                <TableHead className="w-[180px] hidden lg:table-cell">
                  Conditions
                </TableHead>
                <TableHead
                  className="w-[100px] hidden lg:table-cell"
                  align="center"
                >
                  Options
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-64" />
                      <div className="lg:hidden">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  if (handoffRules.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="relative w-full max-w-128 md:max-w-96">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search handoff rules..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={handleCreate} variant="form" className="sm:ml-6">
            Add handoff rule
          </Button>
        </div>

        <EmptyState
          icon={<UserCheck className="h-8 w-8" />}
          title="No handoff rules yet"
          description="Create rules to automatically route calls to the right people or send notifications."
          actionLabel="Create your first handoff rule"
          onAction={handleCreate}
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
        <div className="relative max-w-96">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search handoff rules..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={handleCreate} variant="form">
          Add handoff rule
        </Button>
      </div>

      {filteredHandoffRules.length === 0 ? (
        <div className="border rounded-lg p-8">
          <p className="text-sm text-muted-foreground text-center">
            {searchQuery
              ? 'No handoff rules found matching your search.'
              : 'No handoff rules yet. Create your first one to get started.'}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Handoff rule</TableHead>
                <TableHead className="w-[180px] hidden lg:table-cell">
                  Actions
                </TableHead>
                <TableHead className="w-[180px] hidden lg:table-cell">
                  Conditions
                </TableHead>
                <TableHead
                  className="w-[100px] hidden lg:table-cell"
                  align="center"
                >
                  Options
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHandoffRules.map(rule => (
                <TableRow
                  key={rule.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleEdit(rule)}
                >
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{rule.title}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-md">
                        {rule.selectionCriteria || 'No selection criteria'}
                      </div>
                      <div className="lg:hidden">
                        <div className="flex flex-wrap gap-1 items-center mt-2">
                          {getActionsSummary(rule)}
                          {getConditionsSummary(rule)}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {getActionsSummary(rule)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {getConditionsSummary(rule)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Options for ${rule.title}`}
                          onClick={e => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={e => {
                            e.stopPropagation()
                            handleEdit(rule)
                          }}
                          data-testid={`edit-handoff-rule-${rule.id}`}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit handoff rule
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={e => {
                            e.stopPropagation()
                            duplicateWorkflow(rule.id)
                          }}
                          data-testid={`duplicate-handoff-rule-${rule.id}`}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate handoff rule
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={e => {
                            e.stopPropagation()
                            deleteWorkflow(rule.id)
                          }}
                          data-testid={`delete-handoff-rule-${rule.id}`}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete handoff rule
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export function AgentsActionsPage() {
  useDocumentHead({
    title: 'Actions & Skills',
    description: 'Configure agent handoff rules and action links',
  })

  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [properties, setProperties] = useState<Property[]>([])

  useEffect(() => {
    if (!currentCompany?.id) return

    const loadProperties = async () => {
      try {
        const fetched = isSuperAdmin
          ? await propertiesApiClient.getAllAdminPropertiesForCompany(
              currentCompany.id,
            )
          : await propertiesApiClient.getProperties()
        setProperties(fetched)
      } catch (error) {
        console.error('Error loading properties:', error)
        toast.error('Failed to load properties')
      }
    }

    loadProperties()
  }, [currentCompany?.id, isSuperAdmin])

  return (
    <DirectoryProvider>
      <HandoffWorkflowsProvider properties={properties}>
        <div className="h-full overflow-y-auto pt-16">
          <div className="space-y-6 p-6 pt-0 pb-24 mt-6">
            <PageHeader title="Configure and manage your actions" />
            <HandoffRulesTable />
          </div>
        </div>
      </HandoffWorkflowsProvider>
    </DirectoryProvider>
  )
}
